import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { allComics, chaptersData } from '@/mocks/comics';

const comicSchema = (comic: typeof allComics[0]) => ({
  '@context': 'https://schema.org',
  '@type': 'Book',
  name: `${comic.title} - 免费下拉式漫画在线阅读 - 土豪漫画网`,
  author: { '@type': 'Person', name: comic.author },
  description: comic.description,
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: comic.rating,
    bestRating: 10,
    ratingCount: parseInt(comic.views) || 100000,
  },
  genre: comic.category,
  inLanguage: 'zh-CN',
});

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'desc' | 'chapters'>('chapters');
  const [readingChapter, setReadingChapter] = useState<number | null>(null);
  const [selectedSort, setSelectedSort] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const numericId = parseInt(id || '1');
  const comic = allComics.find((c) => c.id === numericId);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <i className="ri-file-unknow-line text-6xl text-foreground-300"></i>
            <h1 className="text-2xl font-bold text-foreground-950 mt-6 whitespace-nowrap">漫画未找到</h1>
            <p className="text-foreground-500 mt-2">该漫画可能已被下架或链接有误</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const chapters = chaptersData[comic.id] || Array.from({ length: comic.chapters }, (_, i) => ({
    id: i + 1,
    title: `第${i + 1}话`,
    time: `${Math.floor(i / 3) + 1}天前`,
  }));

  const sortedChapters = selectedSort === 'asc' ? [...chapters].reverse() : chapters;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(comicSchema(comic)) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            {readingChapter ? (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => setReadingChapter(null)}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i> 返回详情
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => readingChapter > 1 && setReadingChapter(readingChapter - 1)}
                      className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-700 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer disabled:opacity-40"
                      disabled={readingChapter <= 1}
                    >
                      <i className="ri-arrow-up-s-line"></i>
                    </button>
                    <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                      第 {readingChapter} 话
                    </span>
                    <button
                      onClick={() => readingChapter < (comic.chapters || 1) && setReadingChapter(readingChapter + 1)}
                      className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-700 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer disabled:opacity-40"
                      disabled={readingChapter >= (comic.chapters || 1)}
                    >
                      <i className="ri-arrow-down-s-line"></i>
                    </button>
                  </div>
                  <div className="w-20"></div>
                </div>
                <div className="bg-background-100 rounded-2xl p-6 md:p-10 flex flex-col items-center">
                  <div className="max-w-2xl w-full space-y-4">
                    {Array.from({ length: 8 }, (_, i) => (
                      <div
                        key={i}
                        className="bg-background-200 rounded-xl overflow-hidden flex items-center justify-center"
                        style={{
                          height: `${(i % 3) * 60 + 240}px`,
                        }}
                      >
                        <div className="text-center text-foreground-300">
                          <i className="ri-image-line text-4xl mb-2 block"></i>
                          <p className="text-sm">漫画内容 - 第{readingChapter}话 第{i + 1}页</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-8">
                    <button
                      onClick={() => readingChapter > 1 && setReadingChapter(readingChapter - 1)}
                      className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                      disabled={readingChapter <= 1}
                    >
                      <i className="ri-arrow-left-s-line mr-1"></i>上一话
                    </button>
                    <button
                      onClick={() => setReadingChapter(null)}
                      className="px-5 py-2.5 rounded-full bg-background-200 text-foreground-700 text-sm font-medium hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      返回目录
                    </button>
                    <button
                      onClick={() => readingChapter < (comic.chapters || 1) && setReadingChapter(readingChapter + 1)}
                      className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                      disabled={readingChapter >= (comic.chapters || 1)}
                    >
                      下一话<i className="ri-arrow-right-s-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="md:col-span-1">
                    <div className="relative overflow-hidden rounded-xl aspect-[3/4] sticky top-24">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                          comic.status === '连载中'
                            ? 'bg-accent-500 text-white'
                            : 'bg-foreground-700 text-white'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                      {comic.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      <Link
                        to={`/category/${comic.categorySlug}`}
                        className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap hover:bg-secondary-200 transition-colors"
                      >
                        {comic.category}
                      </Link>
                      <span className="text-sm text-foreground-600 whitespace-nowrap">作者：<strong>{comic.author}</strong></span>
                      <span className="text-sm text-foreground-500">|</span>
                      <span className="text-sm text-foreground-600 whitespace-nowrap flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <strong>{comic.rating}</strong> 分
                      </span>
                      <span className="text-sm text-foreground-500">|</span>
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        <i className="ri-eye-line mr-1"></i>{comic.views} 阅读
                      </span>
                      <span className="text-sm text-foreground-500">|</span>
                      <span className="text-sm text-foreground-600 whitespace-nowrap">
                        共 {comic.chapters} 话
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mt-6">
                      <button
                        onClick={() => setReadingChapter(selectedSort === 'desc' ? chapters.length : 1)}
                        className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-play-circle-line text-lg"></i>
                        {comic.status === '已完结' ? '从头阅读' : '开始阅读'}
                      </button>
                      <button
                        onClick={() => {
                          const lastChapter = selectedSort === 'desc' ? chapters.length : 1;
                          setReadingChapter(lastChapter);
                        }}
                        className="flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-5 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-skip-forward-line text-lg"></i>
                        最新话
                      </button>
                    </div>

                    <div className="flex items-center gap-1 mt-8 border-b border-background-200/70">
                      <button
                        onClick={() => setActiveTab('chapters')}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                          activeTab === 'chapters'
                            ? 'border-primary-500 text-primary-500'
                            : 'border-transparent text-foreground-600 hover:text-foreground-950'
                        }`}
                      >
                        章节列表
                      </button>
                      <button
                        onClick={() => setActiveTab('desc')}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                          activeTab === 'desc'
                            ? 'border-primary-500 text-primary-500'
                            : 'border-transparent text-foreground-600 hover:text-foreground-950'
                        }`}
                      >
                        作品介绍
                      </button>
                    </div>

                    {activeTab === 'chapters' && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-foreground-500 whitespace-nowrap">共 {chapters.length} 话</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setSelectedSort('asc')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                                selectedSort === 'asc'
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                              }`}
                            >
                              正序
                            </button>
                            <button
                              onClick={() => setSelectedSort('desc')}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                                selectedSort === 'desc'
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                              }`}
                            >
                              倒序
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-96 overflow-y-auto pr-1">
                          {sortedChapters.map((ch) => (
                            <button
                              key={ch.id}
                              onClick={() => setReadingChapter(ch.id)}
                              className="text-left px-3 py-2 rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-600 text-sm text-foreground-700 transition-colors truncate whitespace-nowrap cursor-pointer"
                            >
                              {ch.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {activeTab === 'desc' && (
                      <div className="mt-4 text-sm text-foreground-700 leading-relaxed">
                        <p>{comic.description}</p>
                        <div className="mt-6 p-4 bg-background-100 rounded-xl">
                          <h4 className="font-semibold text-foreground-950 mb-2 whitespace-nowrap">作品信息</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs text-foreground-600">
                            <p>类型：{comic.category}</p>
                            <p>作者：{comic.author}</p>
                            <p>状态：{comic.status}</p>
                            <p>评分：{comic.rating}</p>
                            <p>阅读量：{comic.views}</p>
                            <p>总章节：{comic.chapters} 话</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <section className="mt-14">
                  <h3 className="text-xl font-bold text-foreground-950 mb-6 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {allComics
                      .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
                      .slice(0, 6)
                      .map((c) => (
                        <Link
                          key={c.id}
                          to={`/comic/${c.id}`}
                          className="group"
                        >
                          <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                            <img
                              src={c.cover}
                              alt={c.title}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                          </div>
                          <h4 className="text-xs font-semibold text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
                            {c.title}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-0.5">{c.author}</p>
                        </Link>
                      ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}